"use client";

import { FC, useState, useEffect } from "react";
import Link from "next/link";
import { Theme, themeRootClassMap } from "apps/website/maps/Theme.map";
import Image from "apps/website/components/base/Image/Image";
import TextSubtitle from "apps/website/components/base/Text/TextSubtitle/TextSubtitle";
import Icon from "apps/website/components/base/Icon/Icon";
import cx from "apps/website/utils/misc/cx";
import {
  IAnnouncement,
} from "apps/website/components/page/AnnouncementBanner/AnnouncementBanner";
import {
  useAnnouncementCarousel,
} from "apps/website/hooks/useAnnouncementCarousel";

import styles from "./SignupPromotionalBanner.module.css";

export const BANNER_HEIGHT_PX = 44;

const getEntryClassName = (
  animateOnEntry: boolean,
  hasAnimated: boolean,
): string => {
  if (!animateOnEntry) {
    return "";
  }
  return hasAnimated ? styles.bannerUnfurl : styles.bannerHidden;
};

const renderTextComponent = (text: string) => (
  <TextSubtitle
    tag="span"
    size={3}
    align="center"
    className="whitespace-nowrap"
    data-testid="SignupPromotionalBannerText"
  >
    { text }
  </TextSubtitle>
);

const renderAnnouncementContent = (
  announcement: IAnnouncement,
  textComponent: (text: string) => JSX.Element,
) => (
  <>
    { announcement.showTag && (
      <Icon icon="filledTag" size="xsmall" className="mr-1 shrink-0" />
    ) }
    { textComponent(announcement.text) }
    { announcement.image?.src && (
      <div className="pl-2 w-[100px] hidden lg:block">
        <Image image={announcement.image} alt={announcement.text} />
      </div>
    ) }
  </>
);

export interface ISignupPromotionalBanner {
  announcements: IAnnouncement[];
  theme?: Theme;
  className?: string;
  animateOnEntry?: boolean;
}

const SignupPromotionalBanner: FC<ISignupPromotionalBanner> = ({
  announcements,
  theme = "mid-green",
  className = "",
  animateOnEntry = false,
}) => {
  const [ hasAnimated, setHasAnimated ] = useState(false);

  const { currentIndex, isFading, isVisible } = useAnnouncementCarousel({
    totalAnnouncements: announcements.length,
  });

  useEffect(() => {
    if (!animateOnEntry) {
      setHasAnimated(true);
      return;
    }

    const timer = setTimeout(() => {
      setHasAnimated(true);
    }, 100);

    return () => clearTimeout(timer);
  }, [ animateOnEntry ]);

  if (announcements.length === 0) {
    return null;
  }

  const announcement = announcements[currentIndex];
  if (!announcement) {
    return null;
  }

  const entryClassName = getEntryClassName(animateOnEntry, hasAnimated);
  const content = renderAnnouncementContent(announcement, renderTextComponent);

  return (
    <div
      data-component="SignupPromotionalBanner"
      data-testid="SignupPromotionalBanner"
      className={cx(
        "w-full h-[44px] flex justify-center items-center overflow-hidden xl:mt-4",
        themeRootClassMap[theme],
        entryClassName,
        className,
      )}
      data-theme={theme}
    >
      <div
        className={cx(
          "flex flex-row items-center justify-center whitespace-nowrap px-4 transition-opacity duration-500",
          isFading ? "opacity-0" : "opacity-100",
        )}
        aria-hidden={isVisible ? "false" : "true"}
      >
        { announcement.to ? (
          <Link
            href={announcement.to}
            tabIndex={isVisible ? 0 : -1}
            className="flex items-center justify-center"
          >
            { content }
          </Link>
        ) : content }
      </div>
    </div>
  );
};

export default SignupPromotionalBanner;
